import React from 'react';
import {
  compose,
  withHandlers,
  withState,
} from 'recompose';
import { graphql } from 'react-apollo';
import {
  ALL_TAX_INCLUDED_MSG,
  CONFIRM_CHANGE_PLAN,
  INVALID_BRAINTREE_INSTANCE_MSG,
  PAYMENT_PREVIEW,
  PRIVACY_POLICY_MSG,
  PURCHASE_MSG,
  RENEW_SUBSCRIPTION_MSG,
  SUCESSFULLY_PAYMENT_MSG,
  TERMS_AND_CONDITIONS_MSG,
  TOTAL_AMOUNT_MSG,
} from '../../../constants/localization';
import { Dropin, PaymentMethodPayload } from 'braintree-web-drop-in';
import logError from '../../../utils/logError';
import { toast } from 'react-toastify';
import { OptionProps } from '@apollo/react-hoc/lib/types';
import CustomDropIn from './CustomDropIn';
import { messageFromError } from '../../../constants/ui';
import {
  priceCurrencyToString,
  tearDownDropInInstance,
} from '../../../constants/payment';
import {
  MAKE_SUBSCRIPTION,
  
} from '../../../constants/gql/payment';
import ModalActionableBase from '../ModalActionableBase';
import styled from 'styled-components';
import {
  PRIVACY_POLICY_ROUTE,
  TERMS_AND_CONDITIONS_ROUTE,
} from '../../../constants/routes';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

const AdditionalInfo = styled.div`
  display: flex;
  flex-direction: ${isMobile ? 'column-reverse' : 'row'};
  justify-content: space-between;
  margin: ${isMobile ? '0 30px 10px 30px' : '0 50px 20px 50px'};
`;

const LeftInfo = styled.div`
  display: flex;
  flex-direction: column;
  text-align: ${isMobile ? 'center' : 'start'};
  ${isMobile && 'margin-bottom: 10px;'}
  font-size: ${isMobile ? '12px' : '16px'};
`;

const RightInfo = styled.div`
  display: flex;
  flex-direction: column;
  text-align: ${isMobile ? 'center' : 'end'};
  ${isMobile && 'margin-bottom: 10px;'}
`;

const Text = styled.span`
  margin-bottom: 5px;
  text-transform: none;
  white-space: pre-line;
`;

const Lnk = styled(Link)`
  color: white;
  margin-bottom: 5px;
  color: black;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Payment: React.FunctionComponent<IProps> = ({
  header,
  price,
  currency,
  makeSubscription,
  changeSubscriptionsPlans,
  isModalOpen,
  setDropInInstance,
  dropInInstance,
  onCloseModal,
  isDisabled,
  errorMessage,
  hasActivePlan,
  isPendingSubscription,
  isRenewedSubscription,
}) => (
  <ModalActionableBase
    modalStyles={{ height: '120%', width: '120%' }}
    isOpen={isModalOpen}
    onCloseModal={onCloseModal}
    title={`${PAYMENT_PREVIEW} ${header}`}
    confirmAction={
      isPendingSubscription
        ? makeSubscription
        : hasActivePlan
        ? changeSubscriptionsPlans
        : makeSubscription
    }
    confirmText={
      hasActivePlan
        ? CONFIRM_CHANGE_PLAN
        : isRenewedSubscription
        ? RENEW_SUBSCRIPTION_MSG
        : PURCHASE_MSG
    }
    isHideConfirmButton={!dropInInstance}
    confirmButtonId={'submit-button'}
    loading={isDisabled}
  >
    <CustomDropIn
      setDropInInstance={setDropInInstance}
      dropInInstance={dropInInstance}
      errorMessage={errorMessage}
    />
    <AdditionalInfo>
      <LeftInfo>
        <Lnk target={'_tab'} to={PRIVACY_POLICY_ROUTE}>
          <i>{PRIVACY_POLICY_MSG}</i>
        </Lnk>
        <Lnk target={'_tab'} to={TERMS_AND_CONDITIONS_ROUTE}>
          <i>{TERMS_AND_CONDITIONS_MSG}</i>
        </Lnk>
      </LeftInfo>
      <RightInfo>
        <Text>
          {`${TOTAL_AMOUNT_MSG}: `}
          <b>{priceCurrencyToString(price, currency)}</b>
        </Text>
        <Text>{ALL_TAX_INCLUDED_MSG}</Text>
      </RightInfo>
    </AdditionalInfo>
  </ModalActionableBase>
);

export default compose<IInnerProps, IOuterProps>(
  withState('dropInInstance', 'setDropInInstance', null),
  withState('isDisabled', 'setDisabled', false),
  withState('errorMessage', 'setErrorMessage', null),
  withHandlers({
    onCloseModal: ({
      dropInInstance,
      setDropInInstance,
      toggleModal,
      isDisabled,
    }: IProps) => async () => {
      if (isDisabled) {
        return;
      }
      if (dropInInstance) {
        tearDownDropInInstance(dropInInstance, () => setDropInInstance(null));
      }
      toggleModal(false);
    },
  }),
  graphql(MAKE_SUBSCRIPTION, {
    options: { refetchQueries: ['me'] },
    props: ({ mutate, ownProps }: OptionProps<IProps>) => ({
      makeSubscription: async (): Promise<void> => {
        const {
          dropInInstance,
          successfullyPaymentRedirect,
          setDisabled,
          setErrorMessage,
          planId,
          maxPatients,
          isPendingSubscription,
          isRenewedSubscription,
        } = ownProps;
        setDisabled(true);
        if (!dropInInstance) {
          setErrorMessage(INVALID_BRAINTREE_INSTANCE_MSG);
          setDisabled(false);
          return;
        }
        return dropInInstance
          .requestPaymentMethod()
          .then((paymentMethod: PaymentMethodPayload) =>
            mutate({
              variables: {
                nonce: paymentMethod.nonce,
                planId: planId,
                maxPatients: maxPatients,
                isPendingSubscription: isPendingSubscription,
                isRenewedSubscription: isRenewedSubscription,
              },
            }),
          )
          .then((_) => {
            setErrorMessage(null);
            toast(SUCESSFULLY_PAYMENT_MSG, { type: 'info' });
            successfullyPaymentRedirect();
          })
          .catch((error: Error) => {
            logError('FAILED PAYMENT', error);
            setErrorMessage(messageFromError(error));
            dropInInstance.clearSelectedPaymentMethod();
            setDisabled(false);
          });
      },
    }),
  }),
)(Payment);

type IProps = IInnerProps & IOuterProps;

interface IInnerProps {
  makeSubscription: () => Promise<void>;
  changeSubscriptionsPlans: () => void;
  instance: any;
  setInstance: (value: Dropin) => void;
  dropInInstance?: Dropin;
  setDropInInstance: (value: Dropin) => void;
  onCloseModal: () => void;
  setDisabled: (value: boolean) => void;
  isDisabled: boolean;
  errorMessage: string | null;
  setErrorMessage: (value: string) => void;
}

interface IOuterProps {
  isModalOpen: boolean;
  isPatient: boolean;
  maxPatients: number;
  toggleModal: (value: boolean) => void;
  successfullyPaymentRedirect: () => void;
  hasActivePlan: boolean;
  isPendingSubscription: boolean;
  isRenewedSubscription: boolean;
  header: string;
  price: string;
  currency: string;
  planId: string;
}
